import { getEmailServerHeaders } from './emailServerClient';

/**
 * Sends an email notification with form submission details.
 * 
 * This function sends an email to a predefined address with information about
 * a form submission, including the location description and any additional emailBody.
 * 
 * @param {string} locationDesc - The description of the location from the submitted form.
 * @param {string} emailBody - emailBody or additional information to include in the email.
 * @param {string} projectAdminEmail - Optional project admin email to send notification to.
 * @returns {Promise<void>} A promise that resolves when the email is sent successfully, or rejects on failure.
 * @throws {Error} Throws an error if the email sending fails.
 */
export const sendEmailNotification = async (locationDesc: string, emailBody: string, projectAdminEmail?: string): Promise<void> => {
  const recipientEmail = projectAdminEmail || 'hello@theoverhear.app';
  
  const response = await fetch('https://email-server-dot-overhear-2.uc.r.appspot.com/api/send-email', {
    method: 'POST',
    headers: getEmailServerHeaders(),
    body: JSON.stringify({
      to: recipientEmail,
      subject: 'FormsView Submitted',
      text: `The form has been submitted with the following details:
      - Location Description: ${locationDesc}
      - emailBody: ${emailBody}`  // Include emailBody in the email body
    }),
  });

  if (!response.ok) {
    console.error('Failed to send email');
    throw new Error('Failed to send email notification');
  }
};

/**
 * Sends a notification to project admin when a user responds to a recording
 * 
 * @param {string} projectAdminEmail - The project admin's email address
 * @param {string} recordingTitle - The title of the recording that was responded to
 * @param {string} responseText - Optional text response
 * @param {boolean} hasAudioResponse - Whether the response includes audio
 * @param {string} projectName - The name of the project
 * @returns {Promise<void>} A promise that resolves when the email is sent successfully
 * @throws {Error} Throws an error if the email sending fails
 */
export const sendResponseNotification = async (
  projectAdminEmail: string,
  recordingTitle: string,
  responseText?: string,
  hasAudioResponse: boolean = false,
  projectName?: string
): Promise<void> => {
  const responseType = hasAudioResponse 
    ? (responseText ? 'text and audio' : 'audio')
    : 'text';
  
  const responseContent = responseText 
    ? `Response Text: "${responseText}"`
    : '';

  const response = await fetch('https://email-server-dot-overhear-2.uc.r.appspot.com/api/send-email', {
    method: 'POST',
    headers: getEmailServerHeaders(),
    body: JSON.stringify({
      to: projectAdminEmail,
      subject: `New Response to Recording: ${recordingTitle}`,
      text: `
New Response Received

A user has submitted a ${responseType} response to one of your recordings.

PROJECT: ${projectName || 'Unknown Project'}
RECORDING: ${recordingTitle}

${responseContent}
${hasAudioResponse ? 'The response includes an audio file. Please check the admin console to listen.' : ''}

You can view and moderate this response in your project admin console.

Best regards,
The Overhear Team
      `.trim()
    }),
  });

  if (!response.ok) {
    console.error('Failed to send response notification');
    throw new Error('Failed to send response notification');
  }
};

/**
 * Sends an invitation email to join an organisation.
 * @param {string} email - The recipient's email address.
 * @param {string} accessCode - The access code for joining the organisation.
 * @param {string} orgName - The name of the organisation.
 * @param {MemberRole} role - The role being assigned.
 * @param {string} inviterName - The name of the person sending the invitation.
 * @returns {Promise<boolean>} - True if successful, false otherwise.
 */
export const sendBudgetSummary = async (params: {
  email: string;
  budgetSummary: string;
  grandTotal: number;
  generatedDate: string;
  validUntil: string;
}): Promise<void> => {
  const { email, budgetSummary, grandTotal } = params;

  const response = await fetch('https://email-server-dot-overhear-2.uc.r.appspot.com/api/send-email', {
    method: 'POST',
    headers: getEmailServerHeaders(),
    body: JSON.stringify({
      to: email,
      subject: `Your Project Budget Summary - £${grandTotal.toFixed(2)}`,
      text: `
Thank you for your interest in our services!

Please find your detailed project budget summary below:

${budgetSummary}

---

NEXT STEPS:
• This budget is an estimate based on your current selections
• To discuss your requirements in detail, please contact us at info@overhear.co.uk
• We're happy to adjust the budget based on your specific needs
• Formal quotes are available upon request

CONTACT INFORMATION:
• Email: info@overhear.co.uk
• Phone: +44 (0) 20 1234 5678
• Website: www.overhear.co.uk

We look forward to working with you on your project!

Best regards,
The Overhear Team
      `.trim()
    }),
  });

  if (!response.ok) {
    console.error('Failed to send budget summary email');
    throw new Error('Failed to send budget summary email');
  }
};

/**
 * Generic email sending function for newsletters and other email campaigns
 * @param {Object} emailData - Email data object
 * @param {string} emailData.to - Recipient email address
 * @param {string} emailData.subject - Email subject
 * @param {string} emailData.html - HTML content (optional)
 * @param {string} emailData.text - Plain text content
 * @returns {Promise<void>} A promise that resolves when the email is sent successfully
 * @throws {Error} Throws an error if the email sending fails
 */
export const sendEmail = async (emailData: {
  to: string;
  subject: string;
  html?: string;
  text: string;
  customData?: Record<string, any>;
}): Promise<void> => {
  const maxAttempts = 3;
  const initialDelayMs = 750;
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await fetch(
        'https://email-server-dot-overhear-2.uc.r.appspot.com/api/send-email',
        {
          method: 'POST',
          headers: getEmailServerHeaders(),
          body: JSON.stringify({
            to: emailData.to,
            subject: emailData.subject,
            text: emailData.text,
            html: emailData.html,
            customData: emailData.customData,
          }),
        }
      );

      if (response.ok) {
        return;
      }

      const errorText = await response.text();
      const error = new Error(
        `Failed to send email: ${response.status} ${response.statusText || errorText}`
      );

      // Retry transient server / connection failures only
      const isTransient = response.status >= 500 || response.status === 429;
      if (!isTransient || attempt === maxAttempts) {
        console.error('Failed to send email:', {
          status: response.status,
          statusText: response.statusText,
          errorText,
          attempt,
        });
        throw error;
      }

      lastError = error;
      const delayMs = initialDelayMs * Math.pow(2, attempt - 1);
      console.warn(
        `Transient email send failure (attempt ${attempt}/${maxAttempts}), retrying in ${delayMs}ms`
      );
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    } catch (error) {
      if (error instanceof Error && error.message.startsWith('Failed to send email:')) {
        throw error;
      }

      lastError = error instanceof Error ? error : new Error(String(error));
      if (attempt === maxAttempts) {
        throw lastError;
      }

      const delayMs = initialDelayMs * Math.pow(2, attempt - 1);
      console.warn(
        `Network error sending email (attempt ${attempt}/${maxAttempts}), retrying in ${delayMs}ms:`,
        lastError.message
      );
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  throw lastError || new Error('Failed to send email');
};

/**
 * Sends the same email to multiple recipients (e.g. work stream message).
 * @param recipientEmails - Array of recipient email addresses
 * @param subject - Email subject
 * @param text - Plain text body (can include sender name)
 * @returns {Promise<void>} Resolves when all emails are sent; throws if any fail
 */
export const sendEmailToRecipients = async (
  recipientEmails: string[],
  subject: string,
  text: string
): Promise<void> => {
  if (recipientEmails.length === 0) {
    throw new Error('No recipient emails provided');
  }
  for (const to of recipientEmails) {
    await sendEmail({ to, subject, text });
  }
};

/**
 * Sends a file share notification to a colleague (organisation file link).
 * @param to - Recipient email address
 * @param sharerName - Name of the person sharing the file
 * @param fileName - Display name of the file
 * @param fileLink - Full URL to open the file in the app (requires login)
 */
export const sendFileShareNotification = async (
  to: string,
  sharerName: string,
  fileName: string,
  fileLink: string
): Promise<void> => {
  await sendEmail({
    to,
    subject: `${sharerName} shared a file with you: ${fileName}`,
    text: `Hello,

${sharerName} has shared an organisation file with you.

File: ${fileName}

Open the file (you need to be logged in):

${fileLink}

Best regards,
Overhear Admin
`.trim(),
  });
};

/**
 * Sends workshop booking request email to hello@theoverhear.app
 * 
 * @param {Object} bookingData - Workshop booking request data
 * @param {string} audienceGroupName - Name of the audience group that made the booking
 * @param {string} organisationId - Optional organisation ID
 * @returns {Promise<void>} A promise that resolves when the email is sent successfully.
 * @throws {Error} Throws an error if the email sending fails.
 */
